import { z } from 'zod';
import { env } from './env';

const SignInRequest = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type SignInRequest = z.infer<typeof SignInRequest>;

const SignUpRequest = z.object({
  email: z.string().email(),
  password: z.string().min(12),
  name: z.string().min(1),
});
export type SignUpRequest = z.infer<typeof SignUpRequest>;

const AuthSuccessBody = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().optional(),
  }),
  token: z.string().optional(),
});
export type AuthSuccessBody = z.infer<typeof AuthSuccessBody>;

const AuthErrorBody = z.object({
  message: z.string().optional(),
  code: z.string().optional(),
});

export type SignInResult =
  | ({ ok: true } & AuthSuccessBody)
  | { ok: false; status: number; message: string; code?: string };

async function postAuth(path: string, body: unknown): Promise<SignInResult> {
  const res = await fetch(`${env.VITE_AUTH_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = AuthErrorBody.safeParse(json);
    return {
      ok: false,
      status: res.status,
      message: err.success && err.data.message ? err.data.message : `HTTP ${res.status}`,
      ...(err.success && err.data.code ? { code: err.data.code } : {}),
    };
  }
  const parsed = AuthSuccessBody.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      status: res.status,
      message: 'Malformed auth response',
    };
  }
  return { ok: true, ...parsed.data };
}

export function signInEmail(input: SignInRequest): Promise<SignInResult> {
  const parsed = SignInRequest.parse(input);
  return postAuth('/sign-in/email', parsed);
}

export function signUpEmail(input: SignUpRequest): Promise<SignInResult> {
  const parsed = SignUpRequest.parse(input);
  return postAuth('/sign-up/email', parsed);
}

const OAuthRedirectBody = z.object({ url: z.string().url() });

export type SignInWithAuthentikResult =
  | { ok: true; url: string }
  | { ok: false; status: number; message: string };

/**
 * Initiate the Authentik OIDC sign-in. POSTs Better Auth's
 * `genericOAuth` `/sign-in/oauth2` endpoint and returns the provider
 * authorization URL the caller should redirect the browser to. Mirrors the
 * fetch-wrapper style of `signInEmail` — it does NOT navigate, so the caller
 * controls `window.location` (and the call stays unit-testable).
 *
 * `callbackURL` / `errorCallbackURL` default to absolute web-origin URLs so
 * the post-login redirect lands back on the SPA (not the API origin).
 */
export async function signInWithAuthentik(opts?: {
  providerId?: string;
  callbackURL?: string;
  errorCallbackURL?: string;
}): Promise<SignInWithAuthentikResult> {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const body = {
    providerId: opts?.providerId ?? 'authentik',
    callbackURL: opts?.callbackURL ?? `${origin}/chat`,
    errorCallbackURL: opts?.errorCallbackURL ?? `${origin}/signin?error=oauth`,
  };
  const res = await fetch(`${env.VITE_AUTH_BASE_URL}/sign-in/oauth2`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = AuthErrorBody.safeParse(json);
    return {
      ok: false,
      status: res.status,
      message: err.success && err.data.message ? err.data.message : `HTTP ${res.status}`,
    };
  }
  const parsed = OAuthRedirectBody.safeParse(json);
  if (!parsed.success) {
    return { ok: false, status: res.status, message: 'Malformed OAuth response' };
  }
  return { ok: true, url: parsed.data.url };
}

export type SignOutResult = { ok: true } | { ok: false; status: number; message: string };

export async function signOut(): Promise<SignOutResult> {
  const res = await fetch(`${env.VITE_AUTH_BASE_URL}/sign-out`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    return { ok: false, status: res.status, message: `HTTP ${res.status}` };
  }
  return { ok: true };
}

const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(12),
  revokeOtherSessions: z.boolean().optional(),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

export type ChangePasswordResult =
  | { ok: true }
  | { ok: false; status: number; message: string; code?: string };

export async function changePassword(input: ChangePasswordRequest): Promise<ChangePasswordResult> {
  const parsed = ChangePasswordRequest.parse(input);
  const res = await fetch(`${env.VITE_AUTH_BASE_URL}/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(parsed),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
    return {
      ok: false,
      status: res.status,
      message: json.message ?? `HTTP ${res.status}`,
      ...(json.code ? { code: json.code } : {}),
    };
  }
  return { ok: true };
}

const ChangeEmailRequest = z.object({
  newEmail: z.string().email(),
  callbackURL: z.string().url().optional(),
});
export type ChangeEmailRequest = z.infer<typeof ChangeEmailRequest>;

export type ChangeEmailResult = { ok: true } | { ok: false; status: number; message: string };

export async function changeEmail(input: ChangeEmailRequest): Promise<ChangeEmailResult> {
  const parsed = ChangeEmailRequest.parse(input);
  const res = await fetch(`${env.VITE_AUTH_BASE_URL}/change-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(parsed),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { message?: string };
    return {
      ok: false,
      status: res.status,
      message: json.message ?? `HTTP ${res.status}`,
    };
  }
  return { ok: true };
}
